/**
 * Feb 2026 · BOM (Bill of Materials) Master.
 *
 * Every FG Article can carry one or more BOM versions. Only ONE version is
 * marked `is_active` and drives BOM Explosion downstream (Work Order → Net
 * Material Requirement → shortage check → PO trigger).
 *
 * Layout:
 *   • Left column · FG Article picker + list of that article's BOM versions
 *   • Right column · Editor for the selected version (or blank / new form)
 *
 * Backend: /api/boms, /api/boms/{id}/activate, /api/boms/{id}/explode
 */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { RowActions } from "@/components/RowActions";
import SearchablePicker from "@/components/SearchablePicker";
import {
  PlusIcon, TrashIcon, PencilSimpleIcon, CheckCircleIcon,
  MagnifyingGlassIcon, PackageIcon, StackIcon, CalculatorIcon,
  WarningCircleIcon, FloppyDiskIcon, EyeIcon, LockKeyIcon,
  PrinterIcon, XIcon,
} from "@phosphor-icons/react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";

// Feb 2026 · New empty SF-code create form state — kept out of `form` so
// opening the dialog doesn't wipe the parent BOM in-progress. Default UOM
// is kg (SF is typically measured by weight in narrow-fabric operations).
// Mar 2026 · SF authoring moved to the dedicated SF Master page. All
// SF-dialog state, the empty-form template and the createSfCode helper
// were removed from BOM Master so operators can't sidestep the new
// process-aware auto-code recipe.

const inp = "h-9 w-full bg-[var(--bg-0)] border border-[var(--border)] px-3 rounded-sm text-sm focus:outline-none focus:border-[var(--accent)]";
const inpNum = inp + " text-right font-mono tabular-nums";
const btnPrimary = "h-9 px-4 bg-[var(--accent)] hover:opacity-90 text-white text-xs font-semibold rounded-sm flex items-center gap-2";
const btnGhost = "h-9 px-3 border border-[var(--border)] hover:bg-[var(--bg-2)] text-xs font-semibold rounded-sm flex items-center gap-2";

const fmt = (n, d = 2) => Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: d, minimumFractionDigits: d });

export default function BOMMaster() {
  const [articles, setArticles] = useState([]);
  const [rms, setRms] = useState([]);
  const [routingStages, setRoutingStages] = useState([]);
  const [boms, setBoms] = useState([]);        // BOMs for selected article
  // Feb 2026 · Every BOM across every article — used to power SF (semi-finished)
  // sub-BOM picking on line items (BOM Level 2, 3, …). Excludes the currently
  // selected BOM to prevent self-referential cycles.
  const [allBoms, setAllBoms] = useState([]);
  const [selectedArticleId, setSelectedArticleId] = useState("");
  const [selectedBomId, setSelectedBomId] = useState("");
  const [q, setQ] = useState("");
  const [articleOpen, setArticleOpen] = useState(false);   // Feb 2026 · toolbar article picker dropdown
  // Mar 2026 · The `New SF Code` shortcut + dialog were removed — SF
  // authoring lives in the dedicated SF Master page now. When a fresh
  // article is selected and its BOM list is empty, we still auto-open a
  // blank BOM editor if this flag matches the selectedArticleId — kills
  // the setTimeout race that used to leave operators on an empty screen.
  const [pendingNewBomFor, setPendingNewBomFor] = useState("");
  // Feb 2026 · View mode from the BOM Catalog "eye" action. When true the
  // editor renders with a read-only banner and the Save button is hidden
  // so nobody accidentally overwrites a live BOM they only wanted to
  // inspect. Cleared whenever the user starts a new BOM or hits Edit.
  const [readOnlyView, setReadOnlyView] = useState(false);
  const [loading, setLoading] = useState(false);

  // editor state — one form for both create and edit
  const [form, setForm] = useState(null);      // { article_id, version, is_active, output_uom, lines[], notes }
  const [saving, setSaving] = useState(false);

  // explode preview
  const [explodeQty, setExplodeQty] = useState(1000);
  const [explodeResult, setExplodeResult] = useState(null);
  // Feb 2026 · Preview Requirements dialog state — mirrors the explode
  // result so the user can tweak per-line `no_of_ends` before finalising.
  const [previewOpen, setPreviewOpen] = useState(false);
  const [lineOverrides, setLineOverrides] = useState({});
  const [exploding, setExploding] = useState(false);

  // ---------- initial loads ----------
  useEffect(() => {
    (async () => {
      try {
        const [{ data: a }, { data: sfs }, { data: r }, { data: allB }, { data: rs }] = await Promise.all([
          api.get("/articles/"),
          // Mar 2026 · SF split — SF rows live in `sf_master` now. We fetch
          // them separately and merge with FG articles below so the article
          // picker + Level-2 SF selector both continue to work unchanged.
          api.get("/sf-master/").catch(() => ({ data: [] })),
          api.get("/raw-materials/"),
          api.get("/boms/"),
          api.get("/routing-stages/"),
        ]);
        const sfMerged = (sfs || []).map(s => ({
          ...s,
          product_code: s.product_code || s.sf_code || "",
          is_semi_finished: true,
        }));
        setArticles([...(a || []), ...sfMerged]);
        setRms(r || []);
        setAllBoms(allB || []);
        // Feb 2026 · Routing stages back the per-line Routing dropdown.
        // Sort by `sequence` so Warping → Covering → Weaving → Finishing
        // → Packing appears in production order in the picker.
        setRoutingStages((rs || []).filter(s => s.is_active !== false).sort((x, y) => (x.sequence || 0) - (y.sequence || 0)));
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Failed to load masters");
      }
    })();
  }, []);

  // Load BOMs whenever the selected article changes
  useEffect(() => {
    if (!selectedArticleId) { setBoms([]); setSelectedBomId(""); setForm(null); setExplodeResult(null); return; }
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/boms/`, { params: { article_id: selectedArticleId } });
        setBoms(data || []);
        // auto-select active version if present
        const active = (data || []).find(b => b.is_active) || (data || [])[0];
        if (active) { setSelectedBomId(active.id); setForm(active); }
        else if (pendingNewBomFor === selectedArticleId) {
          // Freshly-created SF (or any article the operator wants to
          // author a first BOM for) — open a blank editor instead of
          // dropping them onto an empty screen.
          const art = articles.find(a => a.id === selectedArticleId);
          setSelectedBomId("");
          setForm({
            article_id: selectedArticleId,
            product_code: art?.product_code || "",
            version: 0,
            is_active: true,
            output_uom: art?.output_uom || "m",
            lines: [],
            notes: "",
          });
          setPendingNewBomFor("");
        } else { setSelectedBomId(""); setForm(null); }
        setExplodeResult(null);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Failed to load BOMs");
      } finally { setLoading(false); }
    })();
  }, [selectedArticleId]);   // eslint-disable-line react-hooks/exhaustive-deps

  // ---------- helpers ----------
  // Feb 2026 · Filter out inactive articles from NEW BOM authoring; keep
  // historical BOM versions that already reference retired articles.
  const activeArticles = useMemo(() => (articles || []).filter(a => a.is_active !== false), [articles]);
  const rmActive = useMemo(() => (rms || []).filter(r => r.is_active !== false), [rms]);
  // Fast id → RM lookup so per-line UI can render code / uom / current stock
  // without another O(n) scan on every render.
  const rmById = useMemo(() => Object.fromEntries((rms || []).map(r => [r.id, r])), [rms]);
  // Feb 2026 · RM code quick-lookup — case-insensitive match on code so
  // operators can pull an RM straight from RM Master by typing its code
  // (e.g. "NYLON-40") instead of scrolling a long select.
  const rmByCode = useMemo(() => {
    const m = {};
    for (const r of rmActive) { if (r.code) m[r.code.toLowerCase()] = r; }
    return m;
  }, [rmActive]);
  // Feb 2026 · SF candidates now come from the ARTICLES collection (SF
  // articles carry `is_semi_finished=true`). Picker stores `sf_article_id`
  // — Explode resolves the active BOM at run time so a freshly-created
  // SF code is pickable before its own BOM is authored.
  const sfCandidates = useMemo(
    () => (articles || []).filter(a => a.is_semi_finished === true && a.is_active !== false),
    [articles],
  );
  const sfById = useMemo(() => Object.fromEntries(sfCandidates.map(a => [a.id, a])), [sfCandidates]);
  const sfByCode = useMemo(() => {
    const m = {};
    for (const a of sfCandidates) { if (a.product_code) m[a.product_code.toLowerCase()] = a; }
    return m;
  }, [sfCandidates]);
  const [rmQuick, setRmQuick] = useState("");    // input for pull-up (RM or SF)
  // Search results combine RM Master + SF BOMs so operators can type an
  // "SFC-…" code and add a Level-2 line straight from the header.
  const rmQuickMatches = useMemo(() => {
    const needle = rmQuick.trim().toLowerCase();
    if (!needle) return [];
    const rmHits = rmActive
      .filter(r => (r.code || "").toLowerCase().includes(needle) || (r.name || "").toLowerCase().includes(needle))
      .slice(0, 6)
      .map(r => ({ kind: "RM", id: r.id, code: r.code, name: r.name, uom: r.uom || "kg", ref: r }));
    const sfHits = sfCandidates
      .filter(a => (a.product_code || "").toLowerCase().includes(needle) || (a.description || "").toLowerCase().includes(needle))
      .slice(0, 6)
      .map(a => ({ kind: "SF", id: a.id, code: a.product_code, name: a.description || a.product_code, uom: a.output_uom || "m", ref: a }));
    return [...rmHits, ...sfHits].slice(0, 8);
  }, [rmQuick, rmActive, sfCandidates]);

  const filteredArticles = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const src = activeArticles;
    if (!needle) return src;
    return src.filter(a => (a.product_code || "").toLowerCase().includes(needle) || (a.description || "").toLowerCase().includes(needle));
  }, [activeArticles, q]);

  const selectedArticle = useMemo(() => articles.find(a => a.id === selectedArticleId), [articles, selectedArticleId]);
  // Feb 2026 · Convenience flag — an SF child BOM uses a different column
  // set (% composition instead of No of Ends + Wt/Mtr GMS) in the line
  // table since SF recipes are authored as ratios, not per-metre weights.
  const isSfBom = !!selectedArticle?.is_semi_finished;

  // Feb 2026 · BOM catalog — group every BOM across all articles so
  // operators can see at a glance which Product Codes already have a BOM.
  // Powers the "All BOMs" table + the pill-count on each article-picker
  // dropdown row.
  const bomCountByArticle = useMemo(() => {
    const m = {};
    for (const b of allBoms || []) {
      if (!b.article_id) continue;
      if (!m[b.article_id]) m[b.article_id] = { count: 0, active_version: null, latest_version: 0 };
      m[b.article_id].count += 1;
      if (b.is_active) m[b.article_id].active_version = b.version;
      if ((b.version || 0) > m[b.article_id].latest_version) m[b.article_id].latest_version = b.version;
    }
    return m;
  }, [allBoms]);
  const articleById = useMemo(() => Object.fromEntries((articles || []).map(a => [a.id, a])), [articles]);
  // Flat rows for the catalog table (newest updated first). Denormalised so
  // the UI can render article code + FG/SF badge without a second scan.
  const bomCatalogRows = useMemo(() => {
    return (allBoms || [])
      .map(b => {
        const a = articleById[b.article_id] || {};
        return {
          id: b.id,
          article_id: b.article_id,
          // Sep 03, 2026 · Canonical BOM number aligned with SO/PO.
          // Legacy pre-Sep-03 BOMs read blank until the m0005 backfill
          // migration runs; the "—" fallback keeps the grid tidy.
          bom_no: b.bom_no || "",
          product_code: b.product_code || a.product_code || "—",
          description: a.description || "",
          is_semi_finished: !!a.is_semi_finished,
          version: b.version,
          is_active: !!b.is_active,
          lines: (b.lines || []).length,
          updated_at: b.updated_at || b.created_at || "",
        };
      })
      .sort((x, y) => (y.updated_at || "").localeCompare(x.updated_at || ""));
  }, [allBoms, articleById]);
  const [catalogQ, setCatalogQ] = useState("");
  const [catalogTypeFilter, setCatalogTypeFilter] = useState("ALL");   // ALL | FG | SF
  const filteredCatalog = useMemo(() => {
    const needle = catalogQ.trim().toLowerCase();
    return bomCatalogRows.filter(r => {
      if (catalogTypeFilter === "FG" && r.is_semi_finished) return false;
      if (catalogTypeFilter === "SF" && !r.is_semi_finished) return false;
      if (!needle) return true;
      return (r.bom_no || "").toLowerCase().includes(needle) || (r.product_code || "").toLowerCase().includes(needle) || (r.description || "").toLowerCase().includes(needle);
    });
  }, [bomCatalogRows, catalogQ, catalogTypeFilter]);

  const chooseBom = (b) => { setSelectedBomId(b.id); setForm(b); setExplodeResult(null); setReadOnlyView(false); };
  // Feb 2026 · Catalog action helpers. `openBomFromCatalog` jumps to the
  // BOM's article, waits for the useEffect fetch to populate `boms`, then
  // selects the exact version — used by both View + Edit rows. When
  // opened in read-only mode we ALSO auto-trigger the explosion at qty=1
  // so the Level 1 + Level 2 hierarchy is visible right away — matches
  // the "Like when you Explode BOM" user ask on the View dialog.
  const openBomFromCatalog = (row, { readOnly = false } = {}) => {
    setSelectedArticleId(row.article_id);
    setTimeout(() => {
      const b = (allBoms || []).find(x => x.id === row.id);
      if (b) {
        chooseBom(b);
        setReadOnlyView(readOnly);
        if (readOnly) {
          setExplodeQty(1);
          // Fire explode once the BOM is picked; wrap in a micro-delay so
          // `selectedBomId` state has settled inside `chooseBom`.
          setTimeout(() => {
            api.post(`/boms/${row.id}/explode`, { qty: 1 })
              .then(({ data }) => setExplodeResult(data))
              .catch(err => toast.error(err?.response?.data?.detail || "Auto-explode failed"));
          }, 50);
        }
      }
    }, 250);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  // Close read-only view — clear form so user goes back to the catalog.
  const closeView = () => {
    setForm(null);
    setSelectedBomId("");
    setReadOnlyView(false);
    setExplodeResult(null);
  };
  // Print the current BOM. Blob download so the axios auth header comes
  // through, then a new tab. Object URL revoked after 1 min.
  const printBomPdf = async (bomId) => {
    if (!bomId) return;
    try {
      const res = await api.get(`/boms/${bomId}/pdf`, { params: { qty: 1 }, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "PDF failed");
    }
  };
  const deleteBomFromCatalog = async (row) => {
    if (!window.confirm(`Delete BOM ${row.product_code} · v${row.version}? This cannot be undone.`)) return;
    try {
      await api.delete(`/boms/${row.id}`);
      toast.success("BOM deleted");
      const { data: allB } = await api.get("/boms/");
      setAllBoms(allB || []);
      // If the deleted BOM was the one currently open, clear the editor.
      if (row.id === selectedBomId) { setSelectedBomId(""); setForm(null); }
      // Refresh the per-article BOM list if applicable.
      if (row.article_id === selectedArticleId) {
        const { data } = await api.get("/boms/", { params: { article_id: selectedArticleId } });
        setBoms(data || []);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const newBom = () => {
    if (!selectedArticleId) return toast.error("Pick an FG Article first");
    setSelectedBomId("");
    setReadOnlyView(false);
    setForm({
      article_id: selectedArticleId,
      product_code: selectedArticle?.product_code || "",
      version: 0,             // 0 → backend auto-assigns next
      is_active: boms.length === 0,   // first BOM auto-active
      output_uom: "m",
      lines: [],
      notes: "",
    });
    setExplodeResult(null);
  };

  // Feb 2026 · Every new line defaults to an RM row with the four
  // narrow-fabric spec fields (shade / ends / gms / routing) zeroed so
  // saved payloads stay well-formed. Callers can override with a preset.
  const emptyLine = () => ({
    item_type: "RM",
    rm_id: "",
    sf_article_id: "",
    sf_bom_id: "",
    uom: "kg",
    qty_per_unit: 0,
    wastage_pct: 0,
    shade_ref: "",
    no_of_ends: 0,
    gms_per_metre: 0,
    pct: 0,
    routing: "",
    remarks: "",
  });
  const addLine = (preset = {}) => {
    setForm(f => ({ ...f, lines: [...(f.lines || []), { ...emptyLine(), ...preset }] }));
  };
  // Pull-Up helpers — one call per hit type (RM Master vs. SF BOM).
  const addLineFromRm = (rm) => {
    if (!rm?.id) return;
    addLine({ item_type: "RM", rm_id: rm.id, uom: rm.uom || "kg" });
    setRmQuick("");
    toast.success(`RM line added · ${rm.code}`);
  };
  const addLineFromSf = (sfArticle) => {
    if (!sfArticle?.id) return;
    if (sfArticle.id === form?.article_id) return toast.error("Can't nest an SF inside its own BOM");
    addLine({ item_type: "SF", sf_article_id: sfArticle.id, uom: sfArticle.output_uom || "m" });
    setRmQuick("");
    toast.success(`SF line added · ${sfArticle.product_code}`);
  };
  const removeLine = (idx) => setForm(f => ({ ...f, lines: f.lines.filter((_, i) => i !== idx) }));
  const patchLine = (idx, patch) => setForm(f => ({ ...f, lines: f.lines.map((ln, i) => i === idx ? { ...ln, ...patch } : ln) }));

  // Feb 2026 · Auto-populate a Level-2 SF BOM with two RM lines split by
  // the SF Article's spandex_pct / yarn_pct composition. Operator supplies
  // the total Wt/Mtr (GMS); we distribute it as spandex_grams + yarn_grams
  // and drop in two blank RM rows carrying the correct gms_per_metre — the
  // operator only needs to pick the actual RM code for each row.
  const autoSplitByComposition = () => {
    if (!selectedArticle?.is_semi_finished) return toast.error("Auto-split only works on SF (Semi-Finished) BOMs");
    const spandexPct = Number(selectedArticle.spandex_pct || 0);
    const yarnPct = Number(selectedArticle.yarn_pct || 0);
    if (spandexPct + yarnPct <= 0) return toast.error("SF has no composition % set — edit the SF Code first");
    const raw = window.prompt(
      `Total Wt/Mtr (GMS) for ${selectedArticle.product_code}?\nSpandex ${spandexPct}% + Yarn ${yarnPct}% will be split from this total.`,
      "5",
    );
    if (raw === null) return;
    const total = Number(raw);
    if (!(total > 0)) return toast.error("Enter a positive Wt/Mtr in grams");
    const spandexGms = +(total * spandexPct / 100).toFixed(3);
    const yarnGms = +(total * yarnPct / 100).toFixed(3);
    setForm(f => ({
      ...f,
      lines: [
        ...(f.lines || []),
        { ...emptyLine(), gms_per_metre: spandexGms, routing: "COVERING", remarks: `Auto: Spandex ${spandexPct}%` },
        { ...emptyLine(), gms_per_metre: yarnGms, routing: "COVERING", remarks: `Auto: Yarn ${yarnPct}%` },
      ],
    }));
    toast.success(`Split ${total} g/m → Spandex ${spandexGms} + Yarn ${yarnGms} — pick RMs to complete`);
  };

  const save = async () => {
    if (!form) return;
    if (!form.article_id) return toast.error("Article missing");
    // Feb 2026 · Structural validity — RM lines need rm_id, SF lines need sf_bom_id.
    // Feb 2026 · SF child BOMs use % (composition) instead of Wt/Mtr GMS.
    // A line is valid when its item is referenced AND either a positive
    // percentage (for SF) or a positive Wt/Mtr (for FG parent) is set.
    const cleanLines = (form.lines || []).filter(ln => {
      const kind = ln.item_type || "RM";
      const ref = kind === "SF" ? (ln.sf_article_id || ln.sf_bom_id) : ln.rm_id;
      if (isSfBom) return ref && Number(ln.pct) > 0;
      return ref && Number(ln.gms_per_metre) > 0;
    });
    if (cleanLines.length === 0) return toast.error(isSfBom
      ? "Add at least one line with an item + % > 0"
      : "Add at least one line with an item + Wt/Mtr (GMS) > 0");
    setSaving(true);
    try {
      const payload = {
        article_id: form.article_id,
        product_code: form.product_code || "",
        version: Number(form.version) || 0,
        is_active: !!form.is_active,
        output_uom: form.output_uom || "m",
        notes: form.notes || "",
        lines: cleanLines.map(ln => {
          const kind = ln.item_type || "RM";
          const qtyDefault = kind === "SF" ? 1.0 : 0;
          return {
            item_type: kind,
            rm_id: kind === "RM" ? ln.rm_id : "",
            sf_article_id: kind === "SF" ? (ln.sf_article_id || "") : "",
            sf_bom_id: kind === "SF" ? (ln.sf_bom_id || "") : "",
            uom: kind === "SF"
              ? (sfById[ln.sf_article_id]?.output_uom || ln.uom || "m")
              : (rmById[ln.rm_id]?.uom || ln.uom || "kg"),
            qty_per_unit: Number(ln.qty_per_unit) > 0 ? Number(ln.qty_per_unit) : qtyDefault,
            wastage_pct: Number(ln.wastage_pct) || 0,
            shade_ref: ln.shade_ref || "",
            no_of_ends: Number(ln.no_of_ends) || 0,
            gms_per_metre: Number(ln.gms_per_metre) || 0,
            pct: Number(ln.pct) || 0,
            routing: ln.routing || "",
            remarks: ln.remarks || "",
          };
        }),
      };
      let saved;
      if (selectedBomId) {
        ({ data: saved } = await api.put(`/boms/${selectedBomId}`, payload));
        toast.success(`BOM v${saved.version} updated`);
      } else {
        ({ data: saved } = await api.post(`/boms/`, payload));
        toast.success(`BOM v${saved.version} created`);
      }
      // refresh list
      const { data } = await api.get(`/boms/`, { params: { article_id: form.article_id } });
      setBoms(data || []);
      setSelectedBomId(saved.id);
      setForm(saved);
      // Refresh the SF candidate pool too — a fresh save can create a new
      // sub-BOM candidate that a sibling BOM might reference.
      try { const { data: allB } = await api.get(`/boms/`); setAllBoms(allB || []); } catch {}
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const activate = async (b) => {
    try {
      await api.post(`/boms/${b.id}/activate`);
      toast.success(`v${b.version} activated`);
      const { data } = await api.get(`/boms/`, { params: { article_id: selectedArticleId } });
      setBoms(data || []);
      const active = data.find(x => x.id === b.id);
      if (active) setForm(active);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Activate failed");
    }
  };

  const remove = async (b) => {
    if (!window.confirm(`Delete BOM v${b.version}? This cannot be undone.`)) return;
    try {
      await api.delete(`/boms/${b.id}`);
      toast.success("Deleted");
      const { data } = await api.get(`/boms/`, { params: { article_id: selectedArticleId } });
      setBoms(data || []);
      if (b.id === selectedBomId) { setSelectedBomId(""); setForm(null); }
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  const explode = async () => {
    if (!selectedBomId) return toast.error("Save the BOM first before exploding");
    const qty = Number(explodeQty) || 0;
    if (qty <= 0) return toast.error("Enter a valid output quantity");
    setExploding(true);
    try {
      const { data } = await api.post(`/boms/${selectedBomId}/explode`, { qty });
      setExplodeResult(data);
      setLineOverrides({});
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Explode failed");
    } finally { setExploding(false); }
  };

  // Feb 2026 · Preview Requirements — runs Explode with the current
  // per-line no-of-ends overrides so the user can validate the ×2
  // multiplier for Double covering before finalising.
  const runPreview = async () => {
    const qty = Number(explodeQty) || 1000;
    try {
      const { data } = await api.post(`/boms/${selectedBomId}/explode`, {
        qty, line_overrides: lineOverrides,
      });
      setExplodeResult(data);
      setPreviewOpen(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Preview failed");
    }
  };

  const finalizePreview = async () => {
    await runPreview();          // Recomputes with overrides one last time
    setPreviewOpen(false);
    toast.success("Explosion finalized — overrides locked in");
  };

  // Mar 2026 · `New SF Code` shortcut retired — see SF Master page for
  // process-aware SF authoring (Covering / Spandex Warping / Warping).

  // ---------- render ----------
  return (
    <>
    <div className="p-6 space-y-4" data-testid="bom-master-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="overline mb-1">Bill of Materials</div>
          <h1 className="font-display text-3xl font-black tracking-tight">BOM Master</h1>
          <p className="text-xs text-zinc-500 mt-1">Declare RM consumption per unit of Finished Goods · drives BOM Explosion on Work Order approval</p>
        </div>
      </div>

      {/* Feb 2026 · Full-screen layout — the left sidebar was removed in favour
          of a top toolbar that surfaces the FG Article (Product Code) picker
          + BOM Version selector + primary actions in one strip, freeing the
          full page width for the line-items table. */}
      <div className="tactical-card p-4">
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)_auto] gap-3 items-end">
          {/* ── Product Code / FG Article combobox ── */}
          <div className="relative">
            <div className="overline mb-1 text-[10px]">Product Code · FG Article</div>
            <div className="relative">
              <MagnifyingGlassIcon size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
              <input
                data-testid="bom-article-search"
                value={q}
                onFocus={() => setArticleOpen(true)}
                onBlur={() => setTimeout(() => setArticleOpen(false), 180)}
                onChange={e => { setQ(e.target.value); setArticleOpen(true); }}
                placeholder="Search Article No / description to pick a Product Code…"
                className="w-full h-10 pl-9 pr-3 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm focus:outline-none focus:border-[var(--accent)]"
              />
            </div>
            {/* Feb 2026 · Selected Product Code chip — sits UNDER the search
                input so users can always see what they've picked without
                the value overlapping the search field. Click × to clear. */}
            {selectedArticle && (
              <div data-testid="bom-selected-article-chip"
                   className={`mt-2 flex items-center gap-2 px-3 py-2 bg-[var(--bg-0)] rounded-sm border ${selectedArticle.is_semi_finished ? "border-purple-500/40" : "border-emerald-500/40"}`}>
                {/* Mar 2026 · Two-tone chip — SF/FG token + explicit
                    Level-1/Level-2 authoring hint next to it so operators
                    know at a glance whether the BOM they're about to edit
                    consumes RM only (L1 · FG) or nests an SF (L2 · SF). */}
                <span className={`text-[9px] px-1.5 py-0.5 rounded-sm font-mono uppercase tracking-widest ${selectedArticle.is_semi_finished ? "bg-purple-500/20 text-purple-300 border border-purple-500/40" : "bg-emerald-500/20 text-emerald-300 border border-emerald-500/40"}`}>{selectedArticle.is_semi_finished ? "SF" : "FG"}</span>
                <span data-testid="bom-selected-level-chip"
                      className={`text-[9px] px-1.5 py-0.5 rounded-sm font-mono uppercase tracking-widest ${selectedArticle.is_semi_finished ? "bg-purple-500/10 text-purple-200 border border-purple-500/30" : "bg-emerald-500/10 text-emerald-200 border border-emerald-500/30"}`}
                      title={selectedArticle.is_semi_finished ? "Level-2 BOM · consumes RM to output a Semi-Finished code" : "Level-1 BOM · consumes RM (and optional SF) to output the Finished Good"}>
                  {selectedArticle.is_semi_finished ? "Level 2" : "Level 1"}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="font-mono text-sm text-[var(--accent)] truncate">{selectedArticle.product_code}</div>
                  {selectedArticle.description && <div className="text-[10px] text-zinc-500 truncate">{selectedArticle.description}</div>}
                </div>
                <button
                  data-testid="bom-clear-article"
                  onClick={() => { setSelectedArticleId(""); setQ(""); }}
                  className="text-zinc-500 hover:text-red-400 text-xs font-mono px-2"
                  title="Change Product Code"
                >×</button>
              </div>
            )}
            {articleOpen && filteredArticles.length > 0 && (
              <div className="absolute z-30 top-full left-0 right-0 mt-1 bg-[var(--bg-1)] border border-[var(--border)] rounded-sm shadow-lg max-h-80 overflow-auto">
                {filteredArticles.slice(0, 30).map((a, idx) => {
                  const bomInfo = bomCountByArticle[a.id];
                  return (
                    <button
                      key={a.id}
                      data-testid={`bom-article-${idx}`}
                      data-article-no={a.product_code}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => { setSelectedArticleId(a.id); setQ(""); setArticleOpen(false); }}
                      className={`w-full text-left px-3 py-2 border-b border-[var(--border)] hover:bg-[var(--bg-2)] transition flex items-center gap-2 ${selectedArticleId === a.id ? "bg-[var(--bg-2)]" : ""}`}
                    >
                      <span className={`text-[9px] px-1.5 py-0.5 rounded-sm font-mono uppercase tracking-widest flex-shrink-0 ${a.is_semi_finished ? "bg-purple-500/20 text-purple-300 border border-purple-500/40" : "bg-emerald-500/20 text-emerald-300 border border-emerald-500/40"}`}>{a.is_semi_finished ? "SF" : "FG"}</span>
                      {/* Mar 2026 · Level-1/Level-2 hint per picker row. */}
                      <span className={`text-[9px] px-1.5 py-0.5 rounded-sm font-mono uppercase tracking-widest flex-shrink-0 ${a.is_semi_finished ? "bg-purple-500/10 text-purple-200 border border-purple-500/30" : "bg-emerald-500/10 text-emerald-200 border border-emerald-500/30"}`}>
                        {a.is_semi_finished ? "L2" : "L1"}
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="text-xs font-mono text-[var(--accent)] truncate">{a.product_code || "—"}</div>
                        <div className="text-[10px] text-zinc-500 truncate">{a.description || "—"}</div>
                      </div>
                      {bomInfo ? (
                        <span className="text-[9px] px-1.5 py-0.5 rounded-sm font-mono uppercase tracking-widest flex-shrink-0 bg-[var(--accent)]/15 text-[var(--accent)] border border-[var(--accent)]/30" title={`${bomInfo.count} version(s) · active v${bomInfo.active_version || "—"}`}>
                          {bomInfo.count} BOM{bomInfo.count > 1 ? "s" : ""}{bomInfo.active_version ? ` · v${bomInfo.active_version}` : ""}
                        </span>
                      ) : (
                        <span className="text-[9px] px-1.5 py-0.5 rounded-sm font-mono uppercase tracking-widest flex-shrink-0 border border-dashed border-[var(--border)] text-zinc-500">No BOM</span>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* ── BOM Version selector ── */}
          <div>
            <div className="overline mb-1 text-[10px]">BOM Version</div>
            {selectedArticleId ? (
              boms.length > 0 ? (
                <Select value={selectedBomId || undefined} onValueChange={id => { const b = boms.find(x => x.id === id); if (b) chooseBom(b); }}>
                  <SelectTrigger data-testid="bom-version-select" className="h-10 bg-[var(--bg-0)] border-[var(--border)]"><SelectValue placeholder="Select version…" /></SelectTrigger>
                  <SelectContent className="bg-[var(--bg-1)] border-[var(--border)]">
                    {boms.map(b => (
                      <SelectItem key={b.id} value={b.id} data-testid={`bom-version-${b.version}`}>
                        <span className="font-mono">v{b.version}</span> · {(b.lines || []).length} lines{b.is_active ? " · ACTIVE" : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <div className="h-10 flex items-center px-3 bg-[var(--bg-0)] border border-dashed border-[var(--border)] rounded-sm text-xs text-zinc-500 italic">
                  {loading ? "Loading…" : "No BOM yet · click New Version →"}
                </div>
              )
            ) : (
              <div className="h-10 flex items-center px-3 bg-[var(--bg-0)] border border-dashed border-[var(--border)] rounded-sm text-xs text-zinc-500 italic">Pick a Product Code first</div>
            )}
          </div>

          {/* ── Version actions ── */}
          <div className="flex items-center gap-2">
            {/* Mar 2026 · "+ New SF Code" removed — SF authoring lives in SF Master
                (dedicated `sf_master` collection with process-aware auto codes). */}
            <button
              data-testid="bom-new-version"
              onClick={newBom}
              disabled={!selectedArticleId}
              className={btnPrimary + " disabled:opacity-40 disabled:cursor-not-allowed"}
            >
              <PlusIcon size={12} weight="bold" />New Version
            </button>
            {form && selectedBomId && readOnlyView && (
              <button
                data-testid="bom-toolbar-edit"
                onClick={() => setReadOnlyView(false)}
                className={btnGhost + " border-[var(--accent)]/40 text-[var(--accent)] hover:bg-[var(--accent)]/10"}
                title="Enable editing"
              >
                <PencilSimpleIcon size={12} weight="bold" />Edit
              </button>
            )}
            {form && selectedBomId && !readOnlyView && (
              <button
                data-testid="bom-toolbar-save"
                onClick={save}
                disabled={saving}
                className={btnPrimary + " bg-emerald-600 hover:bg-emerald-500 disabled:opacity-60"}
                title="Save changes to this BOM"
              >
                <FloppyDiskIcon size={12} weight="bold" />{saving ? "Saving…" : "Save"}
              </button>
            )}
            {form && selectedBomId && !form.is_active && (
              <button
                data-testid="bom-activate-current"
                onClick={() => activate({ id: selectedBomId, version: form.version })}
                className={btnGhost + " border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10"}
              >
                <CheckCircleIcon size={12} weight="bold" />Activate
              </button>
            )}
            {form && selectedBomId && (
              <button
                data-testid="bom-delete-current"
                onClick={() => remove({ id: selectedBomId, version: form.version })}
                className={btnGhost + " border-red-500/40 text-red-400 hover:bg-red-500/10"}
              >
                <TrashIcon size={12} weight="bold" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* ─── Full-width Editor + Explode preview ─── */}
      <div className="space-y-4">
        {!form && (
          <div className="tactical-card p-10 text-center text-zinc-500" data-testid="bom-empty-state">
            <StackIcon size={40} weight="light" className="mx-auto mb-3 text-zinc-600" />
            {selectedArticleId ? (
              <div className="space-y-3">
                <div className="text-sm">
                  <span className="font-mono text-[var(--accent)]">{selectedArticle?.product_code}</span> has no BOM yet.
                </div>
                <button
                  data-testid="bom-empty-new-version"
                  onClick={newBom}
                  className={btnPrimary + " mx-auto"}
                >
                  <PlusIcon size={13} weight="bold" />Create {selectedArticle?.is_semi_finished ? "Level-2 " : ""}BOM
                </button>
              </div>
            ) : (
              <div className="text-sm">Pick a Product Code above to view or create a BOM.</div>
            )}
          </div>
        )}

        {form && (
          <>
            <div className={`tactical-card p-4 space-y-3 ${readOnlyView ? "ring-1 ring-blue-500/40" : ""}`} data-testid="bom-editor-card" data-read-only={readOnlyView ? "true" : "false"}>
              {readOnlyView && (
                <div className="flex items-center justify-between gap-3 px-3 py-2 -mx-1 -mt-1 bg-blue-500/10 border border-blue-500/30 rounded-sm">
                  <div className="flex items-center gap-2 text-xs text-blue-300">
                    <LockKeyIcon size={13} weight="fill" />
                    <span className="font-mono uppercase tracking-widest text-[10px]">Read-only view</span>
                    <span className="text-zinc-400">· Level 1 + Level 2 explosion shown below</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      data-testid="bom-view-print"
                      onClick={() => printBomPdf(selectedBomId)}
                      className={btnGhost + " h-7 px-3 border-sky-500/40 text-sky-300 hover:bg-sky-500/10"}
                    >
                      <PrinterIcon size={12} weight="bold" />Print PDF
                    </button>
                    <button
                      data-testid="bom-exit-readonly"
                      onClick={() => setReadOnlyView(false)}
                      className={btnGhost + " h-7 px-3"}
                    >
                      <PencilSimpleIcon size={12} weight="bold" />Switch to Edit
                    </button>
                    <button
                      data-testid="bom-view-close"
                      onClick={closeView}
                      className={btnGhost + " h-7 px-3 border-red-500/40 text-red-300 hover:bg-red-500/10"}
                    >
                      <XIcon size={12} weight="bold" />Close
                    </button>
                  </div>
                </div>
              )}
              <div className={`flex items-center justify-between flex-wrap gap-2 ${readOnlyView ? "opacity-90" : ""}`}>
                <div className="flex items-center gap-4">
                  {/* Mar 2026 · Authoring-Level banner so it's impossible to
                      confuse a Level-1 FG BOM with a Level-2 SF BOM while
                      editing. Colour matches the picker chip so the eye
                      tracks continuity: purple = SF/L2, emerald = FG/L1. */}
                  <div data-testid="bom-editor-level-banner"
                       className={`flex items-center gap-2 px-2.5 py-1.5 rounded-sm border ${isSfBom ? "border-purple-500/50 bg-purple-500/10" : "border-emerald-500/50 bg-emerald-500/10"}`}>
                    <span className={`text-[9px] px-1.5 py-0.5 rounded-sm font-mono uppercase tracking-widest ${isSfBom ? "bg-purple-500/20 text-purple-300 border border-purple-500/40" : "bg-emerald-500/20 text-emerald-300 border border-emerald-500/40"}`}>{isSfBom ? "SF" : "FG"}</span>
                    <span className={`font-mono text-[10px] uppercase tracking-widest ${isSfBom ? "text-purple-200" : "text-emerald-200"}`}>
                      {isSfBom ? "Level 2 · SF Recipe" : "Level 1 · FG Recipe"}
                    </span>
                  </div>
                  <div>
                    <div className="overline text-[10px]">Version</div>
                    <div className="font-mono text-sm text-[var(--accent)]" data-testid="bom-editor-article">{form.bom_no ? <><span className="text-white">{form.bom_no}</span>{" · "}</> : ""}v{form.version || "—"} · {form.product_code || "—"}</div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <label className="flex items-center gap-2 text-xs text-zinc-400">
                    <input
                      data-testid="bom-active-toggle"
                      type="checkbox"
                      checked={!!form.is_active}
                      disabled={readOnlyView}
                      onChange={e => setForm({ ...form, is_active: e.target.checked })}
                    />
                    Active version
                  </label>
                  <div className="text-xs text-zinc-500">Per <b className="text-[var(--accent)]">1 metre</b> of FG · <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-400">Explode = Total Mtrs × Wt/Mtr ÷ 1000 = kg</span></div>
                  {!readOnlyView && (
                    <button data-testid="bom-save-btn" onClick={save} disabled={saving} className={btnPrimary + " disabled:opacity-60"}>
                      <FloppyDiskIcon size={13} weight="bold" />{saving ? "Saving…" : "Save BOM"}
                    </button>
                  )}
                </div>
              </div>

                {/* Feb 2026 · Pull-from-RM-Master OR SF (Semi-Finished BOM)
                    quick adder — type an RM code, name, or SF code and press ↵
                    (or click a match) to append a pre-filled line. Powers
                    both Level-1 RM entries and Level-2 SF nestings. */}
                <div className="border border-[var(--border)] rounded-sm p-3 bg-[var(--bg-2)]/20 relative">
                  <div className="flex items-center justify-between mb-2">
                    <div className="overline text-[10px]">Pull from RM Master / SF · Line Items ({(form.lines || []).length})</div>
                    <div className="flex items-center gap-2">
                      <button data-testid="bom-add-line" onClick={() => addLine()} className={btnGhost + " h-8 px-3"}>
                        <PlusIcon size={12} weight="bold" />Add RM Line
                      </button>
                      <button data-testid="bom-add-sf-line" onClick={() => addLine({ item_type: "SF", uom: "m" })} className={btnPrimary + " h-8 px-3"}>
                        <PlusIcon size={12} weight="bold" />Add SF Line
                      </button>
                    </div>
                  </div>
                  <div className="relative">
                    <MagnifyingGlassIcon size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
                    <input
                      data-testid="bom-rm-quick"
                      value={rmQuick}
                      onChange={e => setRmQuick(e.target.value)}
                      onKeyDown={e => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          const needle = rmQuick.trim().toLowerCase();
                          const rmHit = rmByCode[needle];
                          const sfHit = sfByCode[needle];
                          if (rmHit) addLineFromRm(rmHit);
                          else if (sfHit) addLineFromSf(sfHit);
                          else if (rmQuickMatches.length === 1) {
                            const m = rmQuickMatches[0];
                            m.kind === "SF" ? addLineFromSf(m.ref) : addLineFromRm(m.ref);
                          }
                          else if (rmQuickMatches.length > 1) toast.info(`${rmQuickMatches.length} matches — pick one from the list`);
                          else toast.error("No RM or SF matches");
                        } else if (e.key === "Escape") { setRmQuick(""); }
                      }}
                      placeholder="Type RM Code, name, or SF Code (e.g. SFC-840D…)…  Press ↵ to add"
                      className="w-full h-9 pl-9 pr-3 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm focus:outline-none focus:border-[var(--accent)]"
                    />
                    {rmQuick && rmQuickMatches.length > 0 && (
                      <div data-testid="bom-rm-quick-matches" className="absolute z-30 top-11 left-0 right-0 bg-[var(--bg-1)] border border-[var(--border)] rounded-sm shadow-lg max-h-56 overflow-auto">
                        {rmQuickMatches.map((m, i) => (
                          <button
                            key={`${m.kind}-${m.id}`}
                            data-testid={`bom-rm-quick-match-${i}`}
                            onClick={() => m.kind === "SF" ? addLineFromSf(m.ref) : addLineFromRm(m.ref)}
                            className="w-full text-left px-3 py-2 hover:bg-[var(--bg-2)] border-b border-[var(--border)] last:border-b-0 flex items-center justify-between gap-2"
                          >
                            <div className="min-w-0 flex items-center gap-2">
                              <span className={`text-[9px] px-1.5 py-0.5 rounded-sm font-mono uppercase tracking-widest ${m.kind === "SF" ? "bg-purple-500/20 text-purple-300 border border-purple-500/40" : "bg-emerald-500/20 text-emerald-300 border border-emerald-500/40"}`}>{m.kind}</span>
                              <div className="min-w-0">
                                <div className="font-mono text-xs text-[var(--accent)] truncate">{m.code}</div>
                                <div className="text-[10px] text-zinc-500 truncate">{m.name}</div>
                              </div>
                            </div>
                            <span className="text-[9px] px-2 py-0.5 rounded-full border border-[var(--border)] text-zinc-400 uppercase tracking-widest font-mono flex-shrink-0">{m.uom || "kg"}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div className="border border-[var(--border)] rounded-sm overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-[var(--bg-2)]/50 text-zinc-500 uppercase tracking-widest text-[10px]">
                      <tr>
                        <th className="px-2 py-2 text-center w-10">SR</th>
                        <th className="px-2 py-2 text-center w-14">Type</th>
                        <th className="px-3 py-2 text-left">RM Code / SF Code</th>
                        <th className="px-3 py-2 text-left w-32">Shade Ref</th>
                        {isSfBom ? (
                          <th className="px-3 py-2 text-right w-24">%</th>
                        ) : (
                          <>
                            <th className="px-3 py-2 text-right w-24">No of Ends</th>
                            <th className="px-3 py-2 text-right w-28">Wt/Mtr (GMS)</th>
                          </>
                        )}
                        <th className="px-3 py-2 text-left w-32">Routing</th>
                        <th className="px-3 py-2 text-right w-20">Wastage %</th>
                        <th className="px-2 py-2 text-right w-10">Act</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(form.lines || []).length === 0 && (
                        <tr><td colSpan={isSfBom ? 8 : 9} className="text-center py-8 text-zinc-500 text-xs">No lines yet · click "Add RM Line" / "Add SF Line" above, or Pull from RM Master / SF</td></tr>
                      )}
                      {(form.lines || []).map((ln, i) => {
                        const kind = ln.item_type || "RM";
                        const rm = kind === "RM" ? rmById[ln.rm_id] : null;
                        const sf = kind === "SF" ? sfById[ln.sf_bom_id] : null;
                        const displayUom = kind === "SF" ? (sf?.output_uom || ln.uom || "m") : (rm?.uom || ln.uom || "kg");
                        return (
                          <tr key={i} data-testid={`bom-line-${i}`} data-line-type={kind} className="border-t border-[var(--border)]">
                            <td className="px-2 py-2 text-center font-mono text-xs text-zinc-500">{i + 1}</td>
                            <td className="px-2 py-2 text-center">
                              <select
                                data-testid={`bom-line-${i}-type`}
                                value={kind}
                                onChange={e => patchLine(i, { item_type: e.target.value, rm_id: "", sf_bom_id: "" })}
                                className="h-7 px-1 text-[10px] bg-[var(--bg-0)] border border-[var(--border)] rounded-sm font-mono uppercase tracking-widest"
                              >
                                <option value="RM">RM</option>
                                <option value="SF">SF</option>
                              </select>
                            </td>
                            <td className="px-3 py-2">
                              {kind === "RM" ? (
                                <SearchablePicker
                                  testid={`bom-line-${i}-rm`}
                                  value={ln.rm_id || ""}
                                  onChange={(v) => patchLine(i, { rm_id: v, uom: rmById[v]?.uom || "kg" })}
                                  placeholder="Pick RM…"
                                  headerHint="Search by RM code (e.g. RM-001) or name (e.g. Yarn 70D)"
                                  options={[
                                    // Orphan fallback — historical rm_id not in the live master.
                                    ...(ln.rm_id && !rmById[ln.rm_id] ? [{
                                      value: ln.rm_id,
                                      code: ln.rm_code || "[Unknown]",
                                      name: ln.rm_name || "",
                                      meta: "DELETED",
                                    }] : []),
                                    ...rmActive.map(r => ({
                                      value: r.id,
                                      code: r.code,
                                      name: r.name,
                                      meta: r.category || "",
                                    })),
                                  ]}
                                />
                              ) : (
                                <SearchablePicker
                                  testid={`bom-line-${i}-sf`}
                                  value={ln.sf_article_id || ""}
                                  onChange={(v) => patchLine(i, { sf_article_id: v, sf_bom_id: "", uom: sfById[v]?.output_uom || "m" })}
                                  placeholder="Pick SF Code…"
                                  emptyLabel={sfCandidates.length === 0 ? "No SF codes yet — create one in SF Master." : "No matches — refine your search."}
                                  headerHint="Search by SF Product Code or description"
                                  options={sfCandidates.map(a => ({
                                    value: a.id,
                                    code: a.product_code,
                                    name: a.description || "",
                                  }))}
                                />
                              )}
                            </td>
                            <td className="px-3 py-2">
                              <input data-testid={`bom-line-${i}-shade`} value={ln.shade_ref || ""} onChange={e => patchLine(i, { shade_ref: e.target.value })} placeholder="19-1008 TCX" className={inp} />
                            </td>
                            {isSfBom ? (
                              <td className="px-3 py-2">
                                <input data-testid={`bom-line-${i}-pct`} type="number" step="0.01" min="0" max="100" value={ln.pct || 0} onChange={e => patchLine(i, { pct: e.target.value })} className={inpNum} placeholder="e.g. 85" />
                              </td>
                            ) : (
                              <>
                                <td className="px-3 py-2">
                                  <input data-testid={`bom-line-${i}-ends`} type="number" step="1" min="0" value={ln.no_of_ends || 0} onChange={e => patchLine(i, { no_of_ends: e.target.value })} className={inpNum} />
                                </td>
                                <td className="px-3 py-2">
                                  <input
                                    data-testid={`bom-line-${i}-gms`}
                                    type="text"
                                    inputMode="decimal"
                                    value={ln.gms_per_metre === "" || ln.gms_per_metre === undefined ? "0.000" : ln.gms_per_metre}
                                    onChange={e => patchLine(i, { gms_per_metre: e.target.value })}
                                    onBlur={e => {
                                      // Feb 2026 · Uniform 3-decimal display across all Wt/Mtr
                                      // (GMS) inputs — normalise on blur so 7.6 renders as 7.600.
                                      const n = Number(e.target.value);
                                      patchLine(i, { gms_per_metre: Number.isFinite(n) ? n.toFixed(3) : "0.000" });
                                    }}
                                    className={inpNum}
                                  />
                                </td>
                              </>
                            )}
                            <td className="px-3 py-2">
                              <select
                                data-testid={`bom-line-${i}-routing`}
                                value={ln.routing || ""}
                                onChange={e => patchLine(i, { routing: e.target.value })}
                                className={inp + " uppercase"}
                              >
                                <option value="">— Routing —</option>
                                {routingStages.map(s => (
                                  <option key={s.code} value={s.code}>{s.name}</option>
                                ))}
                              </select>
                            </td>
                            <td className="px-3 py-2">
                              <input data-testid={`bom-line-${i}-wastage`} type="number" step="0.01" min="0" max="100" value={ln.wastage_pct} onChange={e => patchLine(i, { wastage_pct: e.target.value })} className={inpNum} />
                            </td>
                            <td className="px-2 py-2 text-right">
                              <button data-testid={`bom-line-${i}-delete`} onClick={() => removeLine(i)} className="p-1.5 rounded-sm hover:bg-red-500/10 text-red-400">
                                <TrashIcon size={13} />
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                    {(form.lines || []).length > 0 && (
                      <tfoot className="bg-[var(--bg-2)]/30 text-[10px] font-mono uppercase tracking-widest text-zinc-400">
                        <tr className="border-t border-[var(--border)]">
                          {isSfBom ? (
                            <>
                              <td colSpan={4} className="px-3 py-2 text-right">SUM · %</td>
                              <td className="px-3 py-2 text-right font-semibold text-white">
                                {(() => {
                                  const t = (form.lines || []).reduce((s, ln) => s + (Number(ln.pct) || 0), 0);
                                  const inRange = Math.abs(t - 100) < 0.01;
                                  return <span className={inRange ? "text-emerald-400" : (t > 0 ? "text-yellow-400" : "text-zinc-400")}>{fmt(t, 2)} %{inRange ? " ✓" : ""}</span>;
                                })()}
                              </td>
                              <td colSpan={3}></td>
                            </>
                          ) : (
                            <>
                              <td colSpan={5} className="px-3 py-2 text-right">SUM · Wt/Mtr (GMS)</td>
                              <td className="px-3 py-2 text-right font-semibold text-white">
                                {fmt((form.lines || []).reduce((s, ln) => s + (Number(ln.gms_per_metre) || 0), 0), 3)}
                              </td>
                              <td colSpan={3}></td>
                            </>
                          )}
                        </tr>
                      </tfoot>
                    )}
                  </table>
                </div>

                <div className="flex items-center justify-between gap-3">
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500">
                    {(form.lines || []).length} line item{(form.lines || []).length === 1 ? "" : "s"} · multiple line items supported
                  </div>
                  <input
                    data-testid="bom-notes"
                    value={form.notes || ""}
                    onChange={e => setForm({ ...form, notes: e.target.value })}
                    placeholder="Revision notes (optional)…"
                    className={inp + " flex-1"}
                  />
                </div>
              </div>

              {/* Explode preview */}
              <div className="tactical-card p-4 space-y-3">
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div className="flex items-center gap-2">
                    <CalculatorIcon size={16} weight="bold" className="text-[var(--accent)]" />
                    <div className="overline">BOM Explosion Preview</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="text-xs text-zinc-400 flex items-center gap-2">
                      Order Qty ({form.output_uom})
                      <input data-testid="bom-explode-qty" type="number" min="1" step="1" value={explodeQty} onChange={e => setExplodeQty(e.target.value)} className={inpNum + " w-32"} />
                    </label>
                    <button data-testid="bom-explode-btn" onClick={explode} disabled={!selectedBomId || exploding} className={btnPrimary + " disabled:opacity-60"}>
                      <CalculatorIcon size={13} weight="bold" />{exploding ? "Exploding…" : "Explode BOM"}
                    </button>
                    <button data-testid="bom-preview-req-btn" onClick={runPreview} disabled={!selectedBomId}
                      className="px-3 py-1.5 rounded-sm border border-amber-500/50 text-amber-300 hover:bg-amber-500/10 text-xs font-mono uppercase tracking-widest flex items-center gap-1.5 disabled:opacity-40">
                      Preview Requirements
                    </button>
                  </div>
                </div>
                {!selectedBomId && <div className="text-xs text-zinc-500 italic">Save the BOM first, then run explosion to see net material requirement vs current stock.</div>}
                {previewOpen && explodeResult && (
                  <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-6" data-testid="bom-preview-modal">
                    <div className="bg-[var(--bg-1)] border border-[var(--border)] rounded max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col">
                      <div className="px-5 py-3 border-b border-[var(--border)] flex items-center justify-between">
                        <div>
                          <div className="overline text-[10px]">Preview Requirements · Draft BOM Calculation</div>
                          <div className="font-display text-lg text-white">{explodeResult.product_code} · {explodeQty} {form?.output_uom || "m"}</div>
                        </div>
                        <div className="flex items-center gap-2">
                          <button onClick={() => setLineOverrides({})} className="px-3 py-1.5 rounded-sm border border-[var(--border)] text-xs text-zinc-400">Reset Overrides</button>
                          <button onClick={runPreview} className="px-3 py-1.5 rounded-sm border border-sky-500/50 text-sky-300 hover:bg-sky-500/10 text-xs font-mono uppercase tracking-widest">Recompute</button>
                          <button onClick={finalizePreview} data-testid="bom-preview-finalize" className="px-3 py-1.5 rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold">Finalize Explosion</button>
                          <button onClick={() => setPreviewOpen(false)} className="px-3 py-1.5 rounded-sm border border-[var(--border)] text-xs text-zinc-400">Close</button>
                        </div>
                      </div>
                      <div className="overflow-y-auto p-5 space-y-4">
                        {(explodeResult.levels || []).map((sec, sIdx) => (
                          <div key={sIdx} className="border border-[var(--border)] rounded-sm">
                            <div className="px-3 py-2 bg-[var(--bg-2)]/40 flex items-center gap-3 text-xs">
                              <span className="font-mono uppercase tracking-widest text-zinc-500">Level {sec.level}</span>
                              <span className="font-mono text-[var(--accent)]">{sec.product_code}</span>
                              <span className="text-zinc-500">qty {sec.qty} {sec.output_uom}</span>
                              {sec.covering_type && <span className={`px-2 py-0.5 rounded-sm border text-[10px] font-mono uppercase tracking-widest ${sec.covering_type === "DOUBLE" ? "border-orange-500/40 bg-orange-500/10 text-orange-300" : "border-blue-500/40 bg-blue-500/10 text-blue-300"}`}>{sec.covering_type} covering</span>}
                              {sec.sf_no_of_ends > 0 && <span className="text-zinc-400 text-[11px]">SF ends: <b className="font-mono text-white">{sec.sf_no_of_ends}</b></span>}
                            </div>
                            <table className="w-full text-xs">
                              <thead className="bg-[var(--bg-2)]/30 text-[10px] uppercase tracking-widest text-zinc-500">
                                <tr>
                                  <th className="px-3 py-2 text-left">Type</th>
                                  <th className="px-3 py-2 text-left">Item Code</th>
                                  <th className="px-3 py-2 text-right w-24">Required Ends</th>
                                  <th className="px-3 py-2 text-right w-32">Calculated Ends</th>
                                  <th className="px-3 py-2 text-right w-28">Total Length (m)</th>
                                  <th className="px-3 py-2 text-right w-28">Est. Weight (kg)</th>
                                  <th className="px-3 py-2 text-left w-24">UoM</th>
                                </tr>
                              </thead>
                              <tbody>
                                {(sec.lines || []).map((ln, lIdx) => {
                                  const highlight = ln.item_type === "RM" && !ln.is_spandex && sec.covering_type === "DOUBLE";
                                  return (
                                    <tr key={lIdx} className="border-t border-[var(--border)]/60">
                                      <td className="px-3 py-2">
                                        <span className={`text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded-sm border ${ln.item_type === "SF" ? "border-purple-500/40 text-purple-300 bg-purple-500/10" : ln.is_spandex ? "border-pink-500/40 text-pink-300 bg-pink-500/10" : "border-emerald-500/40 text-emerald-300 bg-emerald-500/10"}`}>
                                          {ln.item_type === "SF" ? "SF" : ln.is_spandex ? "SPANDEX" : "YARN"}
                                        </span>
                                      </td>
                                      <td className="px-3 py-2 font-mono text-[var(--accent)]">{ln.item_code}</td>
                                      <td className="px-3 py-2 text-right">
                                        <input type="number" step="1" min="0"
                                          value={lineOverrides[ln.bom_line_id]?.no_of_ends ?? ln.no_of_ends}
                                          onChange={e => setLineOverrides(o => ({ ...o, [ln.bom_line_id]: { no_of_ends: Number(e.target.value) || 0 } }))}
                                          className="w-20 h-7 px-2 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-right font-mono text-white text-xs focus:outline-none focus:border-[var(--accent)]"
                                          data-testid={`bom-preview-ends-${ln.bom_line_id}`} />
                                      </td>
                                      <td className={`px-3 py-2 text-right font-mono tabular-nums font-semibold ${highlight ? "bg-amber-500/20 text-amber-200" : "text-white"}`} title={highlight ? "×2 multiplier applied for Double covering" : ""}>
                                        {ln.calculated_ends}{highlight && <span className="text-[9px] font-mono ml-1 text-amber-300">×2</span>}
                                      </td>
                                      <td className="px-3 py-2 text-right font-mono tabular-nums text-sky-300">{ln.total_length_m || "—"}</td>
                                      <td className="px-3 py-2 text-right font-mono tabular-nums text-emerald-300">{ln.estimated_weight_kg || "—"}</td>
                                      <td className="px-3 py-2 text-zinc-400">{ln.uom}</td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
                {explodeResult && (
                  <div className="space-y-3">
                    <div className="flex items-center gap-4 text-xs flex-wrap">
                      <div><span className="text-zinc-500">Total RM Cost · </span><b className="text-emerald-400 font-mono">₹{fmt(explodeResult.total_cost_inr)}</b></div>
                      <div><span className="text-zinc-500">Shortage Lines · </span><b className={`font-mono ${explodeResult.shortage_line_count ? "text-red-400" : "text-emerald-400"}`}>{explodeResult.shortage_line_count}</b></div>
                      <div><span className="text-zinc-500">Levels · </span><b className="text-purple-300 font-mono">{(explodeResult.levels || []).length}</b></div>
                      {explodeResult.has_shortage && (
                        <div className="flex items-center gap-1 text-red-400"><WarningCircleIcon size={13} weight="fill" /><span className="text-[10px] uppercase font-mono tracking-widest">Material shortage detected</span></div>
                      )}
                    </div>

                    {/* Feb 2026 · Hierarchical view — mirrors the printed BOM sheet
                        (Level 1 → parent lines with SF references, Level 2+ →
                        nested sub-BOMs expanded with their own line items). */}
                    {(explodeResult.levels || []).map((sec, sIdx) => {
                      const sumGms = (sec.lines || []).reduce((s, ln) => s + (Number(ln.gms_per_metre) || 0), 0);
                      return (
                        <div key={sIdx} data-testid={`bom-explode-level-${sec.level}-${sIdx}`} className="border border-[var(--border)] rounded-sm">
                          <div className="px-3 py-2 bg-[var(--bg-2)]/40 border-b border-[var(--border)] flex items-center justify-between gap-2 flex-wrap">
                            <div className="flex items-center gap-2">
                              <span className="text-[9px] px-2 py-0.5 rounded-full bg-purple-500/20 text-purple-300 border border-purple-500/40 font-mono uppercase tracking-widest">BOM Level {sec.level}</span>
                              <span className="font-mono text-xs text-[var(--accent)]">{sec.product_code}</span>
                              {sec.parent_sf_code && <span className="text-[10px] text-zinc-500">(under SF · <span className="font-mono">{sec.parent_sf_code}</span>)</span>}
                            </div>
                            <div className="text-[10px] text-zinc-500 font-mono">
                              Explode Qty · <b className="text-zinc-300">{fmt(sec.qty, 3)}</b> {sec.output_uom}
                            </div>
                          </div>
                          <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                              <thead className="bg-[var(--bg-2)]/20 text-zinc-500 uppercase tracking-widest text-[10px]">
                                <tr>
                                  <th className="px-2 py-2 text-center w-10">SR</th>
                                  <th className="px-2 py-2 text-center w-14">Type</th>
                                  <th className="px-3 py-2 text-left">RM Code / SF Code</th>
                                  <th className="px-3 py-2 text-left w-28">Shade Ref</th>
                                  <th className="px-3 py-2 text-right w-20">Ends</th>
                                  <th className="px-3 py-2 text-right w-24">Wt/Mtr GMS</th>
                                  <th className="px-3 py-2 text-left w-28">Routing</th>
                                  <th className="px-3 py-2 text-right w-28">Gross Req</th>
                                  <th className="px-3 py-2 text-right w-24">Available</th>
                                  <th className="px-3 py-2 text-right w-20">Shortage</th>
                                  <th className="px-3 py-2 text-right w-24">Line Cost</th>
                                </tr>
                              </thead>
                              <tbody>
                                {sec.lines.map((ln, i) => (
                                  <tr key={i} data-testid={`bom-explode-line-${sec.level}-${i}`} data-line-type={ln.item_type} className={`border-t border-[var(--border)] ${(ln.shortage || 0) > 0 ? "bg-red-500/5" : ""}`}>
                                    <td className="px-2 py-2 text-center font-mono text-xs text-zinc-500">{i + 1}</td>
                                    <td className="px-2 py-2 text-center">
                                      <span className={`text-[9px] px-1.5 py-0.5 rounded-sm font-mono uppercase tracking-widest ${ln.item_type === "SF" ? "bg-purple-500/20 text-purple-300 border border-purple-500/40" : "bg-emerald-500/20 text-emerald-300 border border-emerald-500/40"}`}>{ln.item_type}</span>
                                    </td>
                                    <td className="px-3 py-2">
                                      <div className={`font-mono text-xs ${ln.item_type === "SF" ? "text-purple-300" : "text-[var(--accent)]"}`}>{ln.item_code}</div>
                                      <div className="text-[10px] text-zinc-500 truncate max-w-[240px]">{ln.item_name}</div>
                                    </td>
                                    <td className="px-3 py-2 text-[11px] text-zinc-300">{ln.shade_ref || "—"}</td>
                                    <td className="px-3 py-2 text-right font-mono tabular-nums text-zinc-400">{ln.no_of_ends ? fmt(ln.no_of_ends, 0) : "—"}</td>
                                    <td className="px-3 py-2 text-right font-mono tabular-nums text-zinc-300">{ln.gms_per_metre ? fmt(ln.gms_per_metre, 3) : "—"}</td>
                                    <td className="px-3 py-2 text-[11px] font-mono text-zinc-400 uppercase">{ln.routing || "—"}</td>
                                    <td className="px-3 py-2 text-right font-mono tabular-nums text-white font-semibold">{fmt(ln.gross_qty, 3)} {ln.uom}</td>
                                    <td className="px-3 py-2 text-right font-mono tabular-nums text-zinc-400">{ln.item_type === "SF" ? "—" : fmt(ln.available_stock, 3)}</td>
                                    <td className={`px-3 py-2 text-right font-mono tabular-nums font-semibold ${(ln.shortage || 0) > 0 ? "text-red-400" : "text-emerald-400"}`}>{ln.item_type === "SF" ? "—" : ((ln.shortage || 0) > 0 ? fmt(ln.shortage, 3) : "—")}</td>
                                    <td className="px-3 py-2 text-right font-mono tabular-nums text-zinc-300">{ln.item_type === "SF" ? "—" : `₹${fmt(ln.line_cost)}`}</td>
                                  </tr>
                                ))}
                              </tbody>
                              {sumGms > 0 && (
                                <tfoot className="bg-[var(--bg-2)]/20 text-[10px] font-mono uppercase tracking-widest text-zinc-400">
                                  <tr className="border-t border-[var(--border)]">
                                    <td colSpan={5} className="px-3 py-2 text-right">Sum Total · Wt/Mtr (GMS)</td>
                                    <td className="px-3 py-2 text-right font-semibold text-white">{fmt(sumGms, 3)}</td>
                                    <td colSpan={5}></td>
                                  </tr>
                                </tfoot>
                              )}
                            </table>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </>
          )}
        </div>

    {/* Feb 2026 · Sticky Save footer — always visible while scrolling long
        BOM line tables so operators never lose sight of Save. Only renders
        when the editor is open and NOT in read-only mode. */}
    {form && !readOnlyView && (
      <div data-testid="bom-sticky-footer" className="fixed bottom-0 left-0 right-0 z-30 pointer-events-none">
        <div className="mx-auto max-w-[1600px] px-6 pb-4 flex items-center justify-end pointer-events-auto">
          <div className="flex items-center gap-3 bg-[var(--bg-1)]/95 backdrop-blur-md border border-[var(--border)] rounded-sm px-4 py-2 shadow-xl">
            <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-400">
              {selectedBomId ? "Editing" : "New BOM"} · {form.product_code || "—"}{form.bom_no ? ` · ${form.bom_no}` : ""}{form.version ? ` · v${form.version}` : ""}
            </div>
            <button
              data-testid="bom-sticky-save"
              onClick={save}
              disabled={saving}
              className={btnPrimary + " bg-emerald-600 hover:bg-emerald-500 disabled:opacity-60 shadow-lg"}
            >
              <FloppyDiskIcon size={13} weight="bold" />{saving ? "Saving…" : (selectedBomId ? "Save Changes" : "Save BOM")}
            </button>
          </div>
        </div>
      </div>
    )}

    {/* Feb 2026 · BOM Catalog — every Product Code that already carries a
        BOM (FG parents + SF children). Answers the client's "how do I know
        which Product Codes already have a BOM?" ask at a glance. Click a
        row to jump into that BOM. */}
    <div className="tactical-card p-4 space-y-3" data-testid="bom-catalog-card">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <StackIcon size={16} weight="bold" className="text-[var(--accent)]" />
          <div className="overline">All BOMs · {bomCatalogRows.length}</div>
          <span className="text-[10px] text-zinc-500">(FG {bomCatalogRows.filter(r => !r.is_semi_finished).length} · SF {bomCatalogRows.filter(r => r.is_semi_finished).length})</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex bg-[var(--bg-0)] border border-[var(--border)] rounded-sm p-0.5">
            {["ALL", "FG", "SF"].map(t => (
              <button
                key={t}
                data-testid={`bom-catalog-filter-${t.toLowerCase()}`}
                onClick={() => setCatalogTypeFilter(t)}
                className={`px-3 h-7 text-[10px] font-mono uppercase tracking-widest rounded-sm transition ${catalogTypeFilter === t ? "bg-[var(--accent)] text-white" : "text-zinc-400 hover:text-white"}`}
              >{t}</button>
            ))}
          </div>
          <div className="relative">
            <MagnifyingGlassIcon size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-500" />
            <input
              data-testid="bom-catalog-search"
              value={catalogQ}
              onChange={e => setCatalogQ(e.target.value)}
              placeholder="Filter by code / description…"
              className="h-8 w-56 pl-7 pr-3 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-xs focus:outline-none focus:border-[var(--accent)]"
            />
          </div>
        </div>
      </div>
      <div className="border border-[var(--border)] rounded-sm overflow-x-auto max-h-96 overflow-y-auto">
        <table className="w-full text-sm">
          <thead className="bg-[var(--bg-2)]/50 text-zinc-500 uppercase tracking-widest text-[10px] sticky top-0 z-10">
            <tr>
              <th className="px-3 py-2 text-center w-12">Type</th>
              {/* Sep 03, 2026 · Canonical BOM No column — aligns BOM
                  Master with SO / PO / PI grids so operators can
                  cite a BOM by its stable identifier. */}
              <th className="px-3 py-2 text-left w-36">BOM No</th>
              <th className="px-3 py-2 text-left">Product Code</th>
              <th className="px-3 py-2 text-left">Description</th>
              <th className="px-3 py-2 text-right w-20">Version</th>
              <th className="px-3 py-2 text-center w-20">Status</th>
              <th className="px-3 py-2 text-right w-20">Lines</th>
              <th className="px-3 py-2 text-right w-32">Updated</th>
              <th className="px-3 py-2 text-center w-28">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredCatalog.length === 0 && (
              <tr><td colSpan={9} className="text-center py-8 text-xs text-zinc-500">{bomCatalogRows.length === 0 ? "No BOMs authored yet — create one above" : "No BOMs match this filter"}</td></tr>
            )}
            {filteredCatalog.map((r, i) => (
              <tr
                key={r.id}
                data-testid={`bom-catalog-row-${i}`}
                data-article-no={r.product_code}
                className={`border-t border-[var(--border)] hover:bg-[var(--bg-2)]/50 transition ${selectedBomId === r.id ? "bg-[var(--bg-2)]" : ""}`}
              >
                <td className="px-3 py-2 text-center">
                  <span className={`text-[9px] px-1.5 py-0.5 rounded-sm font-mono uppercase tracking-widest ${r.is_semi_finished ? "bg-purple-500/20 text-purple-300 border border-purple-500/40" : "bg-emerald-500/20 text-emerald-300 border border-emerald-500/40"}`}>{r.is_semi_finished ? "SF" : "FG"}</span>
                </td>
                <td className="px-3 py-2 font-mono text-xs text-white tabular-nums" data-testid={`bom-catalog-no-${i}`}>{r.bom_no || <span className="text-zinc-600">—</span>}</td>
                <td className="px-3 py-2 font-mono text-xs text-[var(--accent)] truncate max-w-[420px]">{r.product_code}</td>
                <td className="px-3 py-2 text-[11px] text-zinc-400 truncate max-w-[320px]">{r.description || "—"}</td>
                <td className="px-3 py-2 text-right font-mono tabular-nums">v{r.version}</td>
                <td className="px-3 py-2 text-center">
                  {r.is_active ? (
                    <span className="text-[9px] px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 uppercase tracking-widest font-mono">Active</span>
                  ) : (
                    <span className="text-[9px] px-2 py-0.5 rounded-full border border-[var(--border)] text-zinc-500 uppercase tracking-widest font-mono">Draft</span>
                  )}
                </td>
                <td className="px-3 py-2 text-right font-mono tabular-nums text-zinc-400">{r.lines}</td>
                <td className="px-3 py-2 text-right font-mono text-[10px] text-zinc-500">{(r.updated_at || "").slice(0, 10)}</td>
                <td className="px-3 py-2">
                  <RowActions
                    testid={`bom-catalog-actions-${i}`}
                    items={[
                      { label: "View (read-only)", testid: `bom-catalog-view-${i}`, onClick: () => openBomFromCatalog(r, { readOnly: true }) },
                      { label: "Edit", testid: `bom-catalog-edit-${i}`, onClick: () => openBomFromCatalog(r, { readOnly: false }) },
                      { label: "Delete", tone: "danger", testid: `bom-catalog-delete-${i}`, onClick: () => deleteBomFromCatalog(r) },
                    ]}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
    </div>

    {/* Mar 2026 · SF Code dialog removed — authoring moved to the dedicated
        SF Master page (Product Masters → SF Master). The `+ New SF Code`
        button and this dialog were shortcuts that pre-dated the sf_master
        split; keeping them here would let operators sidestep the new
        process-aware auto-code recipe. */}
    </>
  );
}
